import { useState } from 'react';
import { SEO } from '../../utils/seo';

export default function LandIntelligencePricingPage() {
  const [acreage, setAcreage] = useState(500);
  const [landType, setLandType] = useState('timber');
  const [packageDepth, setPackageDepth] = useState('core');
  const [priorityTurnaround, setPriorityTurnaround] = useState(false);
  const [investorAppendix, setInvestorAppendix] = useState(false);

  // Base pricing ranges per acre
  const basePricing = {
    core: { low: 12, high: 20 },
    expanded: { low: 18, high: 30 },
    full: { low: 25, high: 45 }
  };

  // Calculate multiplier based on acreage
  const calculateMultiplier = (acres: number) => {
    if (acres >= 500) return 1.0;
    if (acres <= 25) return 1.6;
    return 1.6 - ((Math.min(acres, 500) - 25) * (0.6 / 475));
  };

  // Calculate total price
  const calculatePrice = () => {
    const multiplier = calculateMultiplier(acreage);
    const basePrice = basePricing[packageDepth as keyof typeof basePricing];
    
    let adjustedLow = basePrice.low * multiplier;
    let adjustedHigh = basePrice.high * multiplier;

    // Apply add-ons
    if (priorityTurnaround) {
      adjustedLow *= 1.15;
      adjustedHigh *= 1.15;
    }
    if (investorAppendix) {
      adjustedLow *= 1.20;
      adjustedHigh *= 1.20;
    }

    const totalLow = adjustedLow * acreage;
    const totalHigh = adjustedHigh * acreage;

    return {
      perAcreLow: adjustedLow.toFixed(2),
      perAcreHigh: adjustedHigh.toFixed(2),
      totalLow: totalLow.toFixed(0),
      totalHigh: totalHigh.toFixed(0)
    };
  };

  const price = calculatePrice();

  const faqData = [
    {
      question: 'Why do small parcels cost more per acre?',
      answer: 'Fixed costs like mobilization, flight planning, and reporting don\'t scale linearly. Smaller projects require the same setup as larger ones, which increases the per-acre rate. Above 500 acres, economies of scale bring costs down.'
    },
    {
      question: 'What\'s included in land intelligence services?',
      answer: 'Core packages include timber inventory, terrain analysis, and infrastructure review. Expanded packages add forest health analysis and environmental flags. Full due diligence includes all modules plus investor-ready documentation and compliance review.'
    },
    {
      question: 'How fast are results delivered?',
      answer: 'Standard turnaround is 7-10 business days after flight completion. Priority turnaround delivers results within 72 hours. Weather and access conditions may affect flight scheduling.'
    },
    {
      question: 'How does this help with negotiations?',
      answer: 'Independent, documented data gives you leverage. When timber volume, infrastructure condition, and terrain constraints are measured rather than assumed, you negotiate from a position of knowledge. Bad assumptions cost more than surveys.'
    }
  ];

  const scrollToCalculator = () => {
    document.getElementById('calculator')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <SEO
        title="Land Intelligence Pricing | Instant Due Diligence Estimates"
        description="Estimate pricing for land intelligence and due diligence services. Scalable per-acre pricing and fast quotes."
        keywords="land intelligence pricing, due diligence cost estimate, land survey pricing, pre-acquisition survey cost"
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-emerald-50 via-white to-teal-50 pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Clear Pricing for Land Intelligence
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Estimate your project cost by acreage and scope. Final pricing reflects access, terrain, and asset mix.
            </p>
            <button
              onClick={scrollToCalculator}
              className="inline-block bg-emerald-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Estimate Your Project
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Calculator */}
      <section id="calculator" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left: Inputs */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  Project Calculator
                </h2>
                <p className="text-gray-600">
                  Adjust parameters to estimate your project cost
                </p>
              </div>

              {/* Acreage Input */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Total Acreage
                </label>
                <div className="flex items-center gap-4 mb-3">
                  <input
                    type="range"
                    min="25"
                    max="25000"
                    step="25"
                    value={acreage}
                    onChange={(e) => setAcreage(Number(e.target.value))}
                    className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                  />
                  <input
                    type="number"
                    min="25"
                    max="25000"
                    value={acreage}
                    onChange={(e) => setAcreage(Number(e.target.value))}
                    className="w-28 px-4 py-2 border border-gray-300 rounded-lg text-center font-semibold"
                  />
                </div>
                <div className="flex justify-between text-sm text-gray-500">
                  <span>25 acres</span>
                  <span>25,000 acres</span>
                </div>
              </div>

              {/* Land Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Primary Land Type
                </label>
                <select
                  value={landType}
                  onChange={(e) => setLandType(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg font-medium cursor-pointer"
                >
                  <option value="timber">Timber / Forest</option>
                  <option value="mixed">Mixed Use</option>
                  <option value="agricultural">Agricultural</option>
                  <option value="development">Development / Transitional</option>
                </select>
              </div>

              {/* Package Depth */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Package Depth
                </label>
                <div className="space-y-3">
                  <label className="flex items-center p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-emerald-500 transition-colors">
                    <input
                      type="radio"
                      name="package"
                      value="core"
                      checked={packageDepth === 'core'}
                      onChange={(e) => setPackageDepth(e.target.value)}
                      className="w-5 h-5 text-emerald-600 cursor-pointer"
                    />
                    <div className="ml-3">
                      <div className="font-semibold text-gray-900">Core Intelligence Package</div>
                      <div className="text-sm text-gray-600">Timber, terrain, and infrastructure basics</div>
                    </div>
                  </label>
                  <label className="flex items-center p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-emerald-500 transition-colors">
                    <input
                      type="radio"
                      name="package"
                      value="expanded"
                      checked={packageDepth === 'expanded'}
                      onChange={(e) => setPackageDepth(e.target.value)}
                      className="w-5 h-5 text-emerald-600 cursor-pointer"
                    />
                    <div className="ml-3">
                      <div className="font-semibold text-gray-900">Expanded Intelligence Package</div>
                      <div className="text-sm text-gray-600">Core + forest health and environmental flags (+25%)</div>
                    </div>
                  </label>
                  <label className="flex items-center p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-emerald-500 transition-colors">
                    <input
                      type="radio"
                      name="package"
                      value="full"
                      checked={packageDepth === 'full'}
                      onChange={(e) => setPackageDepth(e.target.value)}
                      className="w-5 h-5 text-emerald-600 cursor-pointer"
                    />
                    <div className="ml-3">
                      <div className="font-semibold text-gray-900">Full Due Diligence Package</div>
                      <div className="text-sm text-gray-600">Complete analysis with compliance review (+50%)</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Add-ons */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Add-ons
                </label>
                <div className="space-y-3">
                  <label className="flex items-center p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="checkbox"
                      checked={priorityTurnaround}
                      onChange={(e) => setPriorityTurnaround(e.target.checked)}
                      className="w-5 h-5 text-emerald-600 rounded cursor-pointer"
                    />
                    <div className="ml-3">
                      <div className="font-semibold text-gray-900">Priority Turnaround (72 hours)</div>
                      <div className="text-sm text-gray-600">+15%</div>
                    </div>
                  </label>
                  <label className="flex items-center p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="checkbox"
                      checked={investorAppendix}
                      onChange={(e) => setInvestorAppendix(e.target.checked)}
                      className="w-5 h-5 text-emerald-600 rounded cursor-pointer"
                    />
                    <div className="ml-3">
                      <div className="font-semibold text-gray-900">Investor / Lender-Ready Appendix</div>
                      <div className="text-sm text-gray-600">+20%</div>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* Right: Results */}
            <div className="lg:sticky lg:top-24 h-fit">
              <div className="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-2xl p-8 text-white shadow-xl">
                <h3 className="text-2xl font-bold mb-6">Estimated Project Cost</h3>
                
                <div className="space-y-4 mb-8">
                  <div className="flex justify-between items-center pb-3 border-b border-white/20">
                    <span className="text-emerald-100">Acres Analyzed</span>
                    <span className="text-2xl font-bold">{acreage.toLocaleString()}</span>
                  </div>
                  
                  <div className="flex justify-between items-center pb-3 border-b border-white/20">
                    <span className="text-emerald-100">Package Selected</span>
                    <span className="font-semibold text-right">
                      {packageDepth === 'core' && 'Core Intelligence'}
                      {packageDepth === 'expanded' && 'Expanded Intelligence'}
                      {packageDepth === 'full' && 'Full Due Diligence'}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center pb-3 border-b border-white/20">
                    <span className="text-emerald-100">Per-Acre Rate</span>
                    <span className="font-semibold">${price.perAcreLow} – ${price.perAcreHigh}</span>
                  </div>
                </div>

                <div className="bg-white/10 rounded-xl p-6 mb-6 backdrop-blur-sm">
                  <div className="text-emerald-100 text-sm mb-2">Total Estimated Range</div>
                  <div className="text-4xl font-bold mb-1">
                    ${Number(price.totalLow).toLocaleString()} – ${Number(price.totalHigh).toLocaleString()}
                  </div>
                </div>

                <p className="text-sm text-emerald-100 mb-6">
                  Final scope depends on access, terrain, and asset mix. This estimate provides a planning baseline.
                </p>

                <div className="space-y-3">
                  <a
                    href="/instant-quote"
                    className="block w-full bg-white text-emerald-700 px-6 py-4 rounded-lg font-semibold text-center hover:bg-emerald-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Request a Firm Quote
                  </a>
                  <a
                    href="/instant-quote"
                    className="block w-full bg-emerald-700 text-white px-6 py-4 rounded-lg font-semibold text-center hover:bg-emerald-800 transition-colors border-2 border-white/20 whitespace-nowrap cursor-pointer"
                  >
                    Schedule a Call
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Pricing Questions
            </h2>
            <p className="text-lg text-gray-600">
              Common questions about land intelligence pricing
            </p>
          </div>

          <div className="space-y-6">
            {faqData.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {faq.question}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
